import MenuTop from "../../../components/menu/top/menu-top";
import Form from "../../../components/form/form";
import storage from "../../../shared/services/storage";
import InfoComponent from "../../../components/info/info";
import { useCallback, useEffect, useState } from "react";
import { createFinancierService, updateFinancierService } from "../../../features/financial/service/financiador-service";
import { createProjectService } from "../../../features/project/service/project-service";
import { createRubricaService } from "../../../features/rubrica/service/rubrica";
import { CreateRubricaPayload, RubricaTypeOption } from "../../../components/modals/rubrica/create-rubrica-modal";
import { PendingRubricaOption } from "../../../components/modals/rubrica/rubrica-modal";
import { useNavigate } from "react-router-dom";
import { projectData } from "../../../features/project/forms/project-data";
import { useAlert } from "../../../components/alert/alert";
import { SEVERITY } from "../../../shared/interfaces/alert";
import { Project } from "../../../shared/interfaces/project";
import { FormProps, FormTypes } from "../../../shared/interfaces/form";
import { Company } from "../../../shared/interfaces/user";
import { FieldRule } from "../../../shared/hooks/useFieldRule";
import { SelectActions } from "../../../shared/interfaces/select";
import {
    fetchCachedCompaniesService,
    fetchCachedFinanciersService,
    fetchCachedRubricaTypesService,
    fetchCachedSearchersListService,
    fetchCachedUserCompanyService,
    invalidateProjectSupportCache,
} from "../../../features/project/service/project-query-cache";
import './style.css';

interface PendingFinancierAction {
    type: 'create' | 'edit';
    optionId: string;
    persistedId?: string;
    payload: {
        name: string;
        description: string;
        idCompany: string;
    };
}

function ProjectAdd() {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [ formData, setFormData ] = useState<FormProps[] | any>();
    const [ formSections, setFormSections ] = useState<any[]>();
    const [ companyId, setCompanyId ] = useState<string>();
    const [ company, setCompany ] = useState<Company>();
    const [ isLoading, setIsLoading ] = useState<boolean>(true);
    const [ isSubmitting, setIsSubmitting ] = useState<boolean>(false);
    const [ pendingFinancierAction, setPendingFinancierAction ] = useState<PendingFinancierAction | null>(null);
    const [ rubricaTypes, setRubricaTypes ] = useState<RubricaTypeOption[]>([]);
    const [ pendingRubricas, setPendingRubricas ] = useState<PendingRubricaOption[]>([]);

    const mapFinancierToOption = useCallback((financier: any) => ({
        id: financier?.id,
        value: financier?.id,
        label: financier?.name,
        name: financier?.name,
        description: financier?.description,
        idCompany: financier?.idCompany ?? null,
    }), []);

    const createPendingFinancierOption = useCallback((name: string, idCompany: string, optionId?: string) => {
        const temporaryId = optionId || `pending-financier-${Date.now()}`;

        return mapFinancierToOption({
            id: temporaryId,
            name,
            description: name,
            idCompany,
        });
    }, [mapFinancierToOption]);

    const handleCreatePendingRubrica = useCallback((payload: CreateRubricaPayload): PendingRubricaOption => {
        const tempId = `pending-rubrica-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const matchedType = payload.typeId
            ? rubricaTypes.find((type) => type.id === payload.typeId)
            : undefined;

        const pending: PendingRubricaOption = {
            tempId,
            name: payload.name,
            description: payload.description || payload.name,
            typeId: payload.typeId ?? null,
            typeName: matchedType?.name || 'Novas',
        };

        setPendingRubricas((current) => [...current, pending]);
        return pending;
    }, [rubricaTypes]);

    // Define regras personalizadas para o projeto
    const projectRules: FieldRule[] = [
        // Exemplo: desativa o campo 'confidencialidade' se o projeto não for confidencial
        {
            targetField: 'confidencialidade',
            disableWhen: {
                fields: ['isConfidencial'],
                condition: (values: any) => values.isConfidencial === false || values.isConfidencial === 'false'
            }
        },
        // Outras regras específicas para o projeto
    ];

    // Função para organizar os dados em seções
   // Função para organizar os dados em seções (CORRIGIDA para ficar igual ao edit e list)
    const organizeFormDataIntoSections = (data: FormProps[]) => {
        const projectFields = ['name', 'principalInvestigatorId', 'initialDate', 'finalDate', 'duration'];
        const financialFields = ['financierId', 'accountabilittyDate', 'valor', 'multiplePayments', 'multiplePaymentsRecurrency'];
        const clientFields = ['companyId'];

        const projectData = data.filter(field => projectFields.includes(field.name));
        const financialData = data.filter(field => financialFields.includes(field.name));
        const clientData = data.filter(field => clientFields.includes(field.name));

        return [
            {
                title: "Informações do Projeto",
                icon: "📋",
                color: "project",
                fields: projectData
            },
            {
                title: "Informações Financeiras", 
                icon: "💰",
                color: "financial",
                fields: financialData
            },
            {
                title: "Informações do Cliente",
                icon: "🏢", 
                color: "client",
                fields: clientData
            }
        ];
    };
    
    const fetchSearchers = useCallback(async () => {
        try {
            const response = await fetchCachedSearchersListService();
            return response?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error("Erro ao buscar pesquisadores:", error);
            return [];
        }
    }, []);

    const fetchFinanciers = useCallback(async () => {
        try {
            const response = await fetchCachedFinanciersService();
            return response?.map(mapFinancierToOption) || [];
        } catch (error) {
            console.error("Erro ao buscar financiadores:", error);
            return [];
        }
    }, [mapFinancierToOption]);

    const fetchListCompanies = useCallback(async () => {
        try {
            const response = await fetchCachedCompaniesService();
            return response?.map(({ name, id}: any) => ({ label: name, value: id }));
        } catch (error) {
            console.error("Erro ao buscar empresas:", error);
            return [];
        }
    }, []);

    const fetchUserCompany = useCallback(async () => {
        try {
            return await fetchCachedUserCompanyService();
        } catch (error) {
            console.error("Erro ao buscar empresa do usuário:", error);
            return null;
        }
    }, []);

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);

        try {
            try {
                const user = await storage.getStorage('user');
                if (user) {
                    const userData = JSON.parse(user);
                    if (userData && userData.company && userData.company.id) {
                        setCompanyId(userData.company.id);
                    }
                }
            } catch (error) {
                console.error("Erro ao obter dados do usuário:", error);
            }
            
            const [searchers, financiers, companies, userCompany, rubricaTypesResponse] = await Promise.all([
                fetchSearchers(),
                fetchFinanciers(),
                fetchListCompanies(),
                fetchUserCompany(),
                fetchCachedRubricaTypesService().catch(() => [])
            ]);

            setCompany(userCompany || undefined);
            setRubricaTypes(
                Array.isArray(rubricaTypesResponse)
                    ? rubricaTypesResponse.map((type: any) => ({ id: type.id, name: type.name }))
                    : []
            );

            const financierActions: SelectActions = {
                create: {
                    label: 'Adicionar financiador',
                    modalTitle: 'Adicionar financiador',
                    confirmLabel: 'Adicionar',
                    onSubmit: async (name: string) => {
                        if (!userCompany?.id) {
                            throw new Error('Empresa do usuário não encontrada');
                        }

                        const companyIdToUse = userCompany.id;
                        const pendingOption = createPendingFinancierOption(name, companyIdToUse);

                        setPendingFinancierAction({
                            type: 'create',
                            optionId: pendingOption.id,
                            payload: {
                                name,
                                description: name,
                                idCompany: companyIdToUse,
                            }
                        });

                        showAlert('Financiador será criado ao salvar o projeto', SEVERITY.INFO);
                        return pendingOption;
                    }
                },
                edit: {
                    label: 'Editar financiador selecionado',
                    modalTitle: 'Editar financiador',
                    confirmLabel: 'Salvar',
                    canShow: (option: any) => option?.idCompany === userCompany?.id,
                    onSubmit: async (option: any, name: string) => {
                        if (!userCompany?.id) {
                            throw new Error('Empresa do usuário não encontrada');
                        }

                        const companyIdToUse = userCompany.id;
                        const optionId = option?.id || option?.value;
                        setPendingFinancierAction((currentAction) => {
                            if (currentAction?.type === 'create' && currentAction.optionId === optionId) {
                                return {
                                    ...currentAction,
                                    payload: {
                                        name,
                                        description: name,
                                        idCompany: companyIdToUse,
                                    }
                                };
                            }

                            return {
                                type: 'edit',
                                optionId,
                                payload: {
                                    name,
                                    description: name,
                                    idCompany: companyIdToUse,
                                }
                            };
                        });

                        showAlert('Alteração do financiador será salva junto com o projeto', SEVERITY.INFO);
                        return createPendingFinancierOption(name, companyIdToUse, optionId);
                    }
                }
            };

            const initialFormData = projectData(
                undefined, 
                [], 
                undefined, 
                searchers, 
                financiers, 
                undefined, 
                undefined, 
                undefined, 
                undefined, 
                companies, 
                userCompany?.name,
                [], // timelineData vazio para criação
                financierActions
            );

            setFormData(initialFormData);
            
            // Organizar dados em seções
            const sections = organizeFormDataIntoSections(initialFormData);
            setFormSections(sections);

        } catch (error) {
            console.error("Erro ao carregar dados do projeto:", error);
            showAlert('Erro ao carregar dados do projeto', SEVERITY.ERROR);
        } finally {
            setIsLoading(false);
        }
    }, [createPendingFinancierOption, fetchSearchers, fetchFinanciers, fetchListCompanies, fetchUserCompany, showAlert]);

    // Callback para lidar com mudanças de valores nos campos
    const handleFieldValueChange = useCallback((fieldName: string, value: any) => {
        // Aqui você pode adicionar qualquer lógica específica para tratar as mudanças de campo
        
        // Se precisar fazer algo específico com esses valores, pode fazer aqui
        // Exemplo: atualizar cálculos, executar lógica adicional, etc.
    }, []);
    
    const handleCheckboxChange = useCallback((value: boolean, fieldName: string) => {
        console.log('Checkbox changed:', value, fieldName);
        
        if (fieldName === 'multiplePayments') {
            setFormData((prevFormData: FormProps[]) => {
                if (!prevFormData || !prevFormData.length) return prevFormData;
                
                // Primeiro, atualizar o valor do checkbox
                const updatedFormData = prevFormData.map(item => {
                    if (item.name === 'multiplePayments') {
                        return { ...item, value: value };
                    }
                    return item;
                });
                
                // Encontrar o índice do campo multiplePayments
                const checkboxIndex = updatedFormData.findIndex(item => item.name === 'multiplePayments');
                
                let finalFormData;
                
                if (value) {
                    // Adicionar campo - verificar se já existe
                    const fieldExists = updatedFormData.some((item: FormProps) => item.name === 'multiplePaymentsRecurrency');
                    
                    if (!fieldExists && checkboxIndex !== -1) {
                        const newField: FormProps = {
                            typeOfField: FormTypes.INPUT,
                            type: 'number',
                            placeholder: 'Recorrência dos pagamentos (em meses)*',
                            rules: {
                                required: 'Recorrência dos pagamentos é obrigatória',
                                min: {
                                    value: 1,
                                    message: 'A recorrência deve ser pelo menos 1 mês'
                                }
                            },
                            value: null,
                            name: 'multiplePaymentsRecurrency',
                            width: 200,
                            height: 55,
                            disabled: false
                        };
                        
                        // Inserir após o campo multiplePayments
                        const insertIndex = checkboxIndex + 1;
                        
                        finalFormData = [
                            ...updatedFormData.slice(0, insertIndex),
                            newField,
                            ...updatedFormData.slice(insertIndex)
                        ];
                    } else {
                        finalFormData = updatedFormData;
                    }
                } else {
                    // Remover campo se existir
                    finalFormData = updatedFormData.filter(item => item.name !== 'multiplePaymentsRecurrency');
                }
                
                // Reorganizar seções após mudança
                const sections = organizeFormDataIntoSections(finalFormData);
                setFormSections(sections);
                
                return finalFormData;
            });
        }
    }, []);

    const onSubmitForm = async (data: Project) => {
        if (isSubmitting) return;
        
        setIsSubmitting(true);
        setIsLoading(true);
        
        try {            
            const projectDataToSubmit = data as Project & { financierId?: string };
            projectDataToSubmit.subProject = null;

            if (pendingFinancierAction && projectDataToSubmit.financierId === pendingFinancierAction.optionId) {
                if (pendingFinancierAction.type === 'create') {
                    if (pendingFinancierAction.persistedId) {
                        projectDataToSubmit.financierId = pendingFinancierAction.persistedId;
                    } else {
                        const createdFinancier = await createFinancierService(pendingFinancierAction.payload);
                        projectDataToSubmit.financierId = createdFinancier.id;
                        invalidateProjectSupportCache();
                        setPendingFinancierAction((currentAction) => currentAction ? {
                            ...currentAction,
                            persistedId: createdFinancier.id,
                        } : currentAction);
                    }
                } else {
                    await updateFinancierService(pendingFinancierAction.optionId, pendingFinancierAction.payload);
                    invalidateProjectSupportCache();
                }
            }

            if (company?.name?.toLocaleLowerCase() !== 'causale') {
                projectDataToSubmit.companyId = companyId;
            }

            // Persistir rubricas criadas inline antes de salvar o projeto
            const submittedRubricas = (projectDataToSubmit as any).rubricas as Array<any> | undefined;
            if (Array.isArray(submittedRubricas) && submittedRubricas.length > 0 && pendingRubricas.length > 0) {
                const tempIds = new Set(pendingRubricas.map((p) => p.tempId));
                const referenced = submittedRubricas.filter((r) => tempIds.has(r.rubricaId));

                if (referenced.length > 0) {
                    const idMap = new Map<string, string>();
                    for (const pending of pendingRubricas) {
                        if (!referenced.some((r) => r.rubricaId === pending.tempId)) continue;
                        const created = await createRubricaService({
                            name: pending.name,
                            description: pending.description,
                            typeId: pending.typeId ?? null,
                            clientId: companyId,
                        });
                        invalidateProjectSupportCache();
                        idMap.set(pending.tempId, created.id);
                    }

                    (projectDataToSubmit as any).rubricas = submittedRubricas.map((r) => {
                        const realId = idMap.get(r.rubricaId);
                        if (!realId) return r;
                        return {
                            ...r,
                            rubricaId: realId,
                            rubrica: r.rubrica ? { ...r.rubrica, id: realId } : r.rubrica,
                        };
                    });
                }
            }

            await createProjectService(projectDataToSubmit);
            setPendingFinancierAction(null);
            setPendingRubricas([]);

            showAlert('Projeto adicionado com sucesso', SEVERITY.SUCCESS);
            navigate('/dashboard', {replace: true});
            
        } catch (error: any) {
            showAlert(
                error.message || 'Erro ao adicionar projeto', 
                SEVERITY.ERROR
            );
        } finally {
            setIsLoading(false);
            setIsSubmitting(false);
        }
    };
    
    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    return (
        <div className="container-show-project">
            <MenuTop />
            <InfoComponent />
            <div className="container-show-project__container">
                <Form
                    data={formData}
                    sections={formSections}
                    title='Adicionar projeto'
                    onSubmitForm={onSubmitForm}
                    onCancel={() => navigate('/dashboard', {replace: true})}
                    isLoading={isLoading}
                    onFieldValueChange={handleFieldValueChange}
                    onChangeCheckbox={handleCheckboxChange}
                    fieldRules={projectRules}
                    rubricaTypes={rubricaTypes}
                    pendingRubricas={pendingRubricas}
                    onCreatePendingRubrica={handleCreatePendingRubrica}
                />
            </div>
        </div>
    );
}

export default ProjectAdd;
